/**
 * Package entity adapter: maps the abstract flow model onto `package_deliveries`,
 * which runs two legs on one row (pickup, then delivery), distinguished by
 * current_leg. Parties: pickup = sender / carrier; delivery = carrier / recipient
 * (the recipient side is modelled on the sender for a two-party conversation).
 */

const legParty = (row, userId) => {
  if (row.current_leg === 'delivery') {
    return userId === row.carrier_id ? 'carrier' : 'recipient';
  }
  return userId === row.sender_id ? 'sender' : 'carrier';
};

const otherLegParty = (row, party) => {
  if (row.current_leg === 'delivery') return party === 'carrier' ? 'recipient' : 'carrier';
  return party === 'sender' ? 'carrier' : 'sender';
};

const accusedId = (row) => {
  switch (row.no_show_party) {
    case 'sender':
      return row.sender_id;
    case 'carrier':
      return row.carrier_id;
    case 'recipient':
      return row.sender_id; // recipient side modelled as the sender
    default:
      return null;
  }
};

export const packageAdapter = {
  entity: 'package',
  table: 'package_deliveries',

  actorKeys(row, userId, ownerId) {
    const keys = ['either'];
    if (userId === ownerId) keys.push('recipient');
    if (row.status === 'disputed_no_show' && userId === accusedId(row)) keys.push('accused');
    return keys;
  },

  abstract(row, { userId, travelDate } = {}) {
    const delivery = row.current_leg === 'delivery';
    const party = userId ? legParty(row, userId) : null;
    // column pickers for the current leg
    const hereFor = (pty) =>
      delivery
        ? pty === 'carrier'
          ? row.delivery_carrier_here_at
          : row.delivery_recipient_here_at
        : pty === 'sender'
          ? row.pickup_sender_here_at
          : row.pickup_carrier_here_at;
    const readyFor = (pty) =>
      delivery
        ? pty === 'carrier'
          ? row.delivery_carrier_ready_time
          : row.delivery_recipient_ready_time
        : pty === 'sender'
          ? row.pickup_sender_ready_time
          : row.pickup_carrier_ready_time;
    const presenceFor = (pty) =>
      delivery
        ? pty === 'carrier'
          ? row.delivery_carrier_present_confirmed_at
          : row.delivery_recipient_present_confirmed_at
        : pty === 'sender'
          ? row.pickup_sender_present_confirmed_at
          : row.pickup_carrier_present_confirmed_at;
    const myArrival = party ? hereFor(party) : null;
    const otherArrival = party ? hereFor(otherLegParty(row, party)) : null;
    const myReady = party ? readyFor(party) : null;
    const otherReady = party ? readyFor(otherLegParty(row, party)) : null;
    const myPresence = party ? presenceFor(party) : null;
    const otherPresence = party ? presenceFor(otherLegParty(row, party)) : null;
    const proposal = delivery ? row.delivery_meeting_proposal : row.pickup_meeting_proposal;
    const proposedBy = proposal ? proposal.by : null;
    return {
      proposedBy,
      iProposed: proposedBy != null && proposedBy === userId,
      iArrived: Boolean(myArrival),
      otherArrived: Boolean(otherArrival),
      iPresenceConfirmed: Boolean(myPresence),
      otherPresenceConfirmed: Boolean(otherPresence),
      iConfirmed: Boolean(myReady),
      otherConfirmed: Boolean(otherReady),
      travelDate: travelDate || null,
      meetingTime: (delivery ? row.delivery_meeting_time : row.pickup_meeting_time) || null,
      disputedAt: row.status === 'disputed_no_show' ? row.updated_at : null,
    };
  },

  contextFor(row, userId, { ownerId, travelDate } = {}) {
    return {
      actorKeys: this.actorKeys(row, userId, ownerId),
      userId,
      row: this.abstract(row, { userId, travelDate }),
    };
  },

  payloadFor(action, { userId, row, meetingTime, reason, now, geo }) {
    const delivery = row.current_leg === 'delivery';
    const T = delivery ? 'delivery' : 'pickup';
    const party = legParty(row, userId);
    const meetingCol = `${T}_meeting_time`;
    const proposalCol = `${T}_meeting_proposal`;
    const hereCol = delivery
      ? party === 'carrier'
        ? 'delivery_carrier_here_at'
        : 'delivery_recipient_here_at'
      : party === 'sender'
        ? 'pickup_sender_here_at'
        : 'pickup_carrier_here_at';
    const readyCol = delivery
      ? party === 'carrier'
        ? 'delivery_carrier_ready_time'
        : 'delivery_recipient_ready_time'
      : party === 'sender'
        ? 'pickup_sender_ready_time'
        : 'pickup_carrier_ready_time';
    const presenceCol = delivery
      ? party === 'carrier'
        ? 'delivery_carrier_present_confirmed_at'
        : 'delivery_recipient_present_confirmed_at'
      : party === 'sender'
        ? 'pickup_sender_present_confirmed_at'
        : 'pickup_carrier_present_confirmed_at';

    switch (action) {
      case 'propose_time':
        return { [meetingCol]: meetingTime, [proposalCol]: { by: userId, time: meetingTime } };
      case 'agree_time': {
        const proposal = delivery ? row.delivery_meeting_proposal : row.pickup_meeting_proposal;
        return { [meetingCol]: (proposal && proposal.time) || row[meetingCol] };
      }
      case 'mark_here': {
        // Advisory location proof: one column per leg, keyed by party inside the
        // jsonb so both parties' proofs coexist (advance_flow jsonb-merges these).
        const out = { [hereCol]: now };
        if (geo) out[`${T}_location_proof`] = { [party]: geo };
        return out;
      }
      case 'confirm_presence':
        return { [presenceCol]: now };
      case 'dispute_presence':
        return {
          no_show_party: otherLegParty(row, party),
          flow_dispute_record: { type: 'presence', disputed_by: userId, accused_party: otherLegParty(row, party), leg: T, at: now },
        };
      case 'cancel_as_no_show':
        return { no_show_party: party };
      case 'confirm_handoff':
        return { [readyCol]: now };
      case 'report_no_show':
        return { no_show_party: otherLegParty(row, party) };
      case 'cancel':
        return { cancellation_reason: reason || 'cancelled' };
      case 'dispute_no_show':
        return {
          cancellation_reason: 'no_show_disputed',
          flow_dispute_record: { type: 'no_show_appeal', disputed_by: userId, leg: T, at: now },
        };
      case 'mark_done':
        return { completion_source: 'self_reported' };
      default:
        return {};
    }
  },

  // Pickup leg's handoff advances to the delivery leg instead of completing.
  nextLegOn(row) {
    if (row.current_leg === 'pickup' && row.status === 'handoff') {
      return { current_leg: 'delivery', status: 'meeting_proposed', action: 'advance_leg' };
    }
    return null;
  },
};
